import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import TemuCategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import {
  isAuthorizedBoundShop,
  loadMergedAuthorizedShopCategories,
  resolveShopsForCategoryFilter,
} from '@/pages/ipe/workbench/utils/listingAutoRuleShopCategories';
import { formatOperatingSites, formatShopListName } from '@/pages/ipe/workbench/utils/platformShopManageUtils';
import {
  LISTING_INTERVAL_DEFAULT_MINUTES,
  LISTING_MODE_SCHEDULED,
  listEnabledListingAutoRules,
  matchListingAutoRuleText,
  normalizeListingMode,
} from '@/pages/ipe/workbench/utils/listingAutoRule';
import SheinTextBatchFilter from '@/shein-platform/components/SheinTextBatchFilter';
import {
  PLATFORM_COLOR_MAP,
  PLATFORM_OPTIONS,
  normalizePlatformCode,
  normalizeSheinShopType,
  normalizeTemuShopType,
  normalizeTiktokShopType,
  platformLabel,
  resolvePlatformColor,
  shopTypeEquals,
} from '@/utils/platformShopCatalog';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import PlatformShopDimensionTabs from '@/components/platform/PlatformShopDimensionTabs';
import { platformAPI } from '@/utils/api';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';
import '@/styles/shein-platform/shein-platform.css';
import '@/styles/shein-platform/shein-product-ops.css';
import '@/styles/shein-platform/shein-ops-pager.css';
import '@/styles/ipe/workbench/modals/IpeDeleteConfirmModal.css';
import '@/styles/ipe/workbench/modals/ListingAutoRulePickModal.css';

const TITLE_ID = 'listing-auto-rule-pick-title';
const SKIP_VALUE = '__skip__';
const PLATFORM_ALL_ID = '';

const PLATFORM_DIMENSION_TABS = [
  { value: PLATFORM_ALL_ID, label: '全部平台' },
  ...PLATFORM_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

const EMPTY_FILTERS = {
  category: '',
  shopId: '',
  shopType: '',
  ruleName: '',
  productName: '',
};

function shopDisplayName(shop) {
  if (!shop) return '';
  const name = formatShopListName(shop.shopName, shop.platformCode);
  if (name && name !== '-') return name;
  return String(shop.name || '').trim();
}

function normalizeShopTypeForFilter(shopType, platformCode) {
  const raw = String(shopType || '').trim();
  if (!raw) return '';
  const code = normalizePlatformCode(platformCode);
  if (code === 'TEMU') return normalizeTemuShopType(raw) || raw;
  if (code === 'SHEIN') return normalizeSheinShopType(raw) || raw;
  if (code === 'TIKTOK') return normalizeTiktokShopType(raw) || raw;
  return raw;
}

/**
 * 选择自动规则弹窗。
 * - ingest：带入本地商品库前选择（可跳过自动化）
 * - publish：商品库「商品刊登」选择规则（不可跳过，需已配置店铺与模板）
 */
export default function ListingAutoRulePickModal({
  open,
  rules = [],
  confirming = false,
  progressMessage = '',
  progressStep = null,
  verifyPlatform = '',
  onConfirm,
  onCancel,
  mode = 'ingest',
  selectedCount = 0,
  banner = null,
  confirmLabel,
  confirmDisabled = false,
  initialSelectedId = '',
  /** 多选初始值（工作流节点已关联多条规则时回显）；优先于 initialSelectedId */
  initialSelectedIds = null,
  /** 「不关联」行的文案（工作流节点复用时语义为「清空关联」） */
  skipTitle,
  skipHint,
  onSelectedChange,
}) {
  const { t } = useTranslation('workbench');
  const skipTitleText = skipTitle || t('listingAutoRule.skipTitle');
  const skipHintText = skipHint || t('listingAutoRule.skipHint');
  const isPublish = mode === 'publish';
  const baseRules = useMemo(() => {
    const enabled = listEnabledListingAutoRules(rules)
      .filter((rule) => rule?.shopId != null && String(rule.shopId).trim() !== '');
    if (!isPublish) return enabled;
    return enabled.filter((rule) => (
      !!rule?.runListing
      && String(rule.productTemplateId || '').trim() !== ''
    ));
  }, [rules, isPublish]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [skipOnly, setSkipOnly] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [selectedPlatformFilter, setSelectedPlatformFilter] = useState(PLATFORM_ALL_ID);
  const [selectedShopFilter, setSelectedShopFilter] = useState(PLATFORM_ALL_SHOPS_ID);
  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  // 类目筛选：仅当前账号已授权店铺的类目并集（指定店铺则只取该店），不走 admin 全局类目
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterCatalogLoading, setFilterCatalogLoading] = useState(false);

  const notifySelection = useCallback((nextIds, nextSkip) => {
    if (typeof onSelectedChange !== 'function') return;
    if (nextSkip) {
      onSelectedChange([]);
      return;
    }
    const idSet = nextIds instanceof Set ? nextIds : new Set(nextIds || []);
    const selectedRules = (Array.isArray(rules) ? rules : []).filter(
      (item) => idSet.has(String(item?.id)),
    );
    onSelectedChange(selectedRules);
  }, [onSelectedChange, rules]);

  const selectSkipOnly = useCallback(() => {
    setSkipOnly(true);
    setSelectedIds(new Set());
    notifySelection(new Set(), true);
  }, [notifySelection]);

  const toggleRule = useCallback((ruleId) => {
    const value = String(ruleId || '');
    if (!value || value === SKIP_VALUE) return;
    setSkipOnly(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      notifySelection(next, false);
      return next;
    });
  }, [notifySelection]);

  const replaceSelection = useCallback((ids, nextSkip = false) => {
    const next = new Set((ids || []).map((id) => String(id)).filter(Boolean));
    setSkipOnly(!!nextSkip);
    setSelectedIds(nextSkip ? new Set() : next);
    notifySelection(nextSkip ? new Set() : next, !!nextSkip);
  }, [notifySelection]);

  const shopMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(shops) ? shops : []).forEach((shop) => map.set(String(shop.id), shop));
    return map;
  }, [shops]);

  const enrichedRules = useMemo(() => {
    const mapped = baseRules.map((rule) => {
      const shop = rule.shopId != null ? shopMap.get(String(rule.shopId)) : null;
      return {
        ...rule,
        shopName: shopDisplayName(shop)
          || String(rule.shopName || '').trim()
          || (rule.shopId ? t('listingSchedule.shopId', { id: rule.shopId }) : ''),
        shopType: rule.shopType || shop?.shopType || '',
        platformCode: normalizePlatformCode(shop?.platformCode || rule.platformCode) || '',
        operatingSites: Array.isArray(shop?.operatingSites) ? shop.operatingSites : [],
      };
    });
    // 店铺列表加载完成后，剔除已删除/未授权店铺上的遗留规则
    if (shopsLoading || shops.length === 0) return mapped;
    return mapped.filter((rule) => {
      const shop = shopMap.get(String(rule.shopId));
      return Boolean(shop) && isAuthorizedBoundShop(shop);
    });
  }, [baseRules, shopMap, shopsLoading, shops.length, t]);

  const authorizedShops = useMemo(
    () => (Array.isArray(shops) ? shops : []).filter(isAuthorizedBoundShop),
    [shops],
  );

  const platformScopedShops = useMemo(() => {
    if (!selectedPlatformFilter) return authorizedShops;
    return authorizedShops.filter(
      (s) => normalizePlatformCode(s.platformCode) === normalizePlatformCode(selectedPlatformFilter),
    );
  }, [authorizedShops, selectedPlatformFilter]);

  const isTemuPlatformFilter = normalizePlatformCode(selectedPlatformFilter) === 'TEMU';
  // 未选平台时类目级联按 SHEIN 组件展示，只合并当前账号已授权的 SHEIN 店类目
  const filterCategoryPlatform = isTemuPlatformFilter
    ? 'TEMU'
    : (normalizePlatformCode(selectedPlatformFilter) || 'SHEIN');

  /** 店铺类型筛选项：来自店铺管理中当前可见店铺的实际类型 */
  const listShopTypeOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    platformScopedShops.forEach((shop) => {
      const type = normalizeShopTypeForFilter(shop.shopType, shop.platformCode);
      if (!type || seen.has(type)) return;
      seen.add(type);
      options.push({ value: type, label: localizedShopTypeLabel(t, type) });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [platformScopedShops, t]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setShopsLoading(true);
    platformAPI.listShops()
      .then((list) => {
        if (!cancelled) setShops(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setShops([]);
      })
      .finally(() => {
        if (!cancelled) setShopsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const shopsForCategories = resolveShopsForCategoryFilter(authorizedShops, {
      shopId: filterDraft.shopId,
      shopType: filterDraft.shopType,
      platformCode: filterCategoryPlatform,
    });
    if (!shopsForCategories.length) {
      setFilterCategories([]);
      setFilterCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFilterCatalogLoading(true);
    loadMergedAuthorizedShopCategories(shopsForCategories)
      .then((tree) => {
        if (!cancelled) setFilterCategories(tree);
      })
      .catch(() => {
        if (!cancelled) setFilterCategories([]);
      })
      .finally(() => {
        if (!cancelled) setFilterCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, [
    open,
    authorizedShops,
    filterDraft.shopId,
    filterDraft.shopType,
    filterCategoryPlatform,
  ]);

  const shopNameFilterOptions = useMemo(
    () => platformScopedShops.map((shop) => ({
      value: String(shop.id),
      label: shopDisplayName(shop) || t('listingSchedule.shopId', { id: shop.id }),
    })),
    [platformScopedShops, t],
  );

  /** 店铺维度 Tab：当前平台下已授权店铺；名称用平台店铺名，计数为规则数 */
  const dimensionShops = useMemo(() => (
    platformScopedShops
      .map((shop) => ({
        id: String(shop.id),
        shopName: shopDisplayName(shop) || t('listingSchedule.shopId', { id: shop.id }),
        shopType: shop.shopType || '',
        platformCode: normalizePlatformCode(shop.platformCode) || '',
      }))
      .sort((a, b) => String(a.shopName).localeCompare(String(b.shopName), 'zh-CN'))
  ), [platformScopedShops, t]);

  const platformCounts = useMemo(() => {
    const counts = { [PLATFORM_ALL_ID]: authorizedShops.length };
    PLATFORM_OPTIONS.forEach((o) => { counts[o.value] = 0; });
    authorizedShops.forEach((shop) => {
      const code = normalizePlatformCode(shop.platformCode);
      if (!code || counts[code] == null) return;
      counts[code] += 1;
    });
    return counts;
  }, [authorizedShops]);

  const shopCounts = useMemo(() => {
    const counts = {};
    platformScopedShops.forEach((shop) => {
      counts[String(shop.id)] = 0;
    });
    let all = 0;
    enrichedRules.forEach((rule) => {
      const id = rule.shopId != null ? String(rule.shopId) : '';
      if (!id || counts[id] == null) return;
      if (selectedPlatformFilter) {
        const code = normalizePlatformCode(rule.platformCode);
        if (code !== normalizePlatformCode(selectedPlatformFilter)) return;
      }
      counts[id] += 1;
      all += 1;
    });
    counts[PLATFORM_ALL_SHOPS_ID] = all;
    return counts;
  }, [enrichedRules, platformScopedShops, selectedPlatformFilter]);

  const filteredRules = useMemo(() => {
    const categoryIdFilter = String(appliedFilters.category || '').trim();
    const shopIdFilter = String(appliedFilters.shopId || '').trim();
    const shopTypeQ = String(appliedFilters.shopType || '').trim();
    const ruleNameQ = String(appliedFilters.ruleName || '').trim();
    const productNameQ = String(appliedFilters.productName || '').trim();
    const platformFilter = normalizePlatformCode(selectedPlatformFilter);

    return enrichedRules.filter((rule) => {
      const id = rule.shopId != null ? String(rule.shopId) : '';
      if (platformFilter) {
        const code = normalizePlatformCode(rule.platformCode);
        if (code !== platformFilter) return false;
      }
      if (!isAllShopsScope(selectedShopFilter) && id !== String(selectedShopFilter)) return false;
      if (shopIdFilter && id !== shopIdFilter) return false;
      if (shopTypeQ && !shopTypeEquals(rule.shopType, shopTypeQ, rule.platformCode || 'SHEIN')) return false;
      if (categoryIdFilter) {
        const ruleCat = String(rule.categoryId || '').trim();
        const rulePt = String(rule.productTypeId || '').trim();
        if (ruleCat !== categoryIdFilter && rulePt !== categoryIdFilter) return false;
      }
      if (ruleNameQ) {
        const hay = `${rule.name || ''} ${rule.id || ''}`;
        if (!matchListingAutoRuleText(hay, ruleNameQ)) return false;
      }
      if (productNameQ) {
        const hay = `${rule.productTemplateName || ''} ${rule.productTemplateId || ''}`;
        if (!matchListingAutoRuleText(hay, productNameQ)) return false;
      }
      return true;
    });
  }, [
    enrichedRules,
    appliedFilters,
    selectedShopFilter,
    selectedPlatformFilter,
  ]);

  const totalFiltered = filteredRules.length;
  const safePageSize = Math.max(1, Number(pageSize) || SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(totalFiltered / safePageSize) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const pagedRules = useMemo(() => {
    const start = (safePage - 1) * safePageSize;
    return filteredRules.slice(start, start + safePageSize);
  }, [filteredRules, safePage, safePageSize]);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters, selectedShopFilter, selectedPlatformFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (!open) return undefined;
    const ruleExists = (id) => (
      enrichedRules.some((rule) => String(rule.id) === String(id))
      || filteredRules.some((rule) => String(rule.id) === String(id))
    );
    const preferredList = (Array.isArray(initialSelectedIds) ? initialSelectedIds : [])
      .map((id) => String(id ?? '').trim())
      .filter((id) => id && id !== SKIP_VALUE && ruleExists(id));
    const preferred = String(initialSelectedId || '').trim();
    const preferredOk = preferred && preferred !== SKIP_VALUE && ruleExists(preferred);
    if (preferredList.length > 0) {
      replaceSelection(preferredList, false);
    } else if (preferredOk) {
      replaceSelection([preferred], false);
    } else if (isPublish) {
      const first = filteredRules[0] || enrichedRules[0];
      replaceSelection(first?.id != null ? [String(first.id)] : [], false);
    } else {
      // 入库默认：仅带入（与规则互斥）
      replaceSelection([], true);
    }
    setSelectedPlatformFilter(PLATFORM_ALL_ID);
    setSelectedShopFilter(PLATFORM_ALL_SHOPS_ID);
    setFilterDraft({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setPage(1);
    let onKeyDown = null;
    const armTimer = window.setTimeout(() => {
      onKeyDown = (event) => {
        if (event.key === 'Escape' && !confirming) onCancel?.();
      };
      window.addEventListener('keydown', onKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(armTimer);
      if (onKeyDown) window.removeEventListener('keydown', onKeyDown);
    };
    // 仅在打开时重置选择与筛选；规则列表变化不强制打断用户操作
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || skipOnly) return;
    if (selectedIds.size === 0) return;
    const visible = new Set(filteredRules.map((rule) => String(rule.id)));
    let changed = false;
    const next = new Set();
    selectedIds.forEach((id) => {
      if (visible.has(id)) next.add(id);
      else changed = true;
    });
    if (!changed) return;
    if (next.size === 0 && isPublish) {
      const first = filteredRules[0];
      replaceSelection(first?.id != null ? [String(first.id)] : [], false);
      return;
    }
    replaceSelection([...next], false);
  }, [open, filteredRules, selectedIds, skipOnly, isPublish, replaceSelection]);

  const onQuery = useCallback(() => {
    setAppliedFilters({ ...filterDraft });
  }, [filterDraft]);

  const onReset = useCallback(() => {
    const empty = { ...EMPTY_FILTERS };
    setFilterDraft(empty);
    setAppliedFilters(empty);
    setSelectedPlatformFilter(PLATFORM_ALL_ID);
    setSelectedShopFilter(PLATFORM_ALL_SHOPS_ID);
    setPage(1);
  }, []);

  const onPlatformTabChange = useCallback((nextPlatform) => {
    setSelectedPlatformFilter(nextPlatform);
    setSelectedShopFilter(PLATFORM_ALL_SHOPS_ID);
    setFilterDraft((prev) => ({ ...prev, shopType: '', shopId: '', category: '' }));
    setAppliedFilters((prev) => ({ ...prev, shopType: '', shopId: '', category: '' }));
    setPage(1);
  }, []);

  const handleConfirm = () => {
    if (skipOnly) {
      if (isPublish) return;
      onConfirm?.({
        listingAutoRuleId: null,
        listingAutoRuleIds: [],
        skip: true,
        rule: null,
        rules: [],
      });
      return;
    }
    const ids = [...selectedIds]
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return;
    // 用 enrichedRules（已按店铺补 platformCode）而不是原始 rules，便于下游判定 TEMU
    const sourceRules = Array.isArray(enrichedRules) && enrichedRules.length
      ? enrichedRules
      : (Array.isArray(rules) ? rules : []);
    const selectedRules = ids
      .map((id) => sourceRules.find((item) => String(item?.id) === String(id)))
      .filter(Boolean);
    onConfirm?.({
      listingAutoRuleId: ids[0],
      listingAutoRuleIds: ids,
      skip: false,
      rule: selectedRules[0] || null,
      rules: selectedRules,
    });
  };

  if (!open) return null;

  const CategorySelect = isTemuPlatformFilter ? TemuCategoryCascaderSelect : CategoryCascaderSelect;
  const footerConfirmLabel = confirming
    ? t('listingAutoRule.verifyingShort')
    : (confirmLabel || (isPublish ? t('listingAutoRule.confirmPublish') : t('listingAutoRule.confirmIngest')));
  const canConfirm = isPublish
    ? selectedIds.size > 0 && !skipOnly && !confirmDisabled
    : (skipOnly || selectedIds.size > 0) && !confirmDisabled;
  const selectedRuleCount = skipOnly ? 0 : selectedIds.size;
  // 核验弹窗文案按所选规则平台区分（TikTok 走发品核验，勿展示 Temu「素材图」步骤）
  const verifyingPlatform = (() => {
    const explicit = normalizePlatformCode(verifyPlatform);
    if (explicit) return explicit;
    const sourceRules = Array.isArray(enrichedRules) && enrichedRules.length
      ? enrichedRules
      : (Array.isArray(rules) ? rules : []);
    const codes = [...selectedIds]
      .map((id) => sourceRules.find((item) => String(item?.id) === String(id)))
      .map((rule) => normalizePlatformCode(rule?.platformCode))
      .filter(Boolean);
    if (!codes.length) return '';
    if (codes.every((code) => code === 'TIKTOK')) return 'TIKTOK';
    if (codes.every((code) => code === 'TEMU')) return 'TEMU';
    if (codes.every((code) => code === 'SHEIN')) return 'SHEIN';
    return codes[0];
  })();
  const verifyingCopy = (() => {
    if (!isPublish) {
      return {
        lead: t('listingAutoRule.verifyLeadIngest'),
        fallbackMsg: t('listingAutoRule.verifyWait'),
        steps: [
          { id: 'apply', label: t('listingAutoRule.stepApply') },
          { id: 'write', label: t('listingAutoRule.stepWrite') },
          { id: 'done', label: t('listingAutoRule.stepIngestDone') },
        ],
      };
    }
    if (verifyingPlatform === 'TIKTOK') {
      return {
        lead: t('listingAutoRule.verifyLeadTiktok'),
        fallbackMsg: t('listingAutoRule.verifyFallbackPublish'),
        steps: [
          { id: 'shop', label: t('listingAutoRule.stepTiktokShop') },
          { id: 'convert', label: t('listingAutoRule.stepTiktokConvert') },
          { id: 'check', label: t('listingAutoRule.stepTiktokCheck') },
        ],
      };
    }
    if (verifyingPlatform === 'SHEIN') {
      return {
        lead: t('listingAutoRule.verifyLeadShein'),
        fallbackMsg: t('listingAutoRule.verifyFallbackShein'),
        steps: [
          { id: 'shop', label: t('listingAutoRule.stepSheinShop') },
          { id: 'convert', label: t('listingAutoRule.stepSheinConvert') },
          { id: 'check', label: t('listingAutoRule.stepSheinCheck') },
        ],
      };
    }
    return {
      lead: t('listingAutoRule.verifyLeadTemu'),
      fallbackMsg: t('listingAutoRule.verifyFallbackPublish'),
      steps: [
        { id: 'shop', label: t('listingAutoRule.stepTemuShop') },
        { id: 'convert', label: t('listingAutoRule.stepTemuConvert') },
        { id: 'psoriasis', label: t('listingAutoRule.stepTemuPsoriasis') },
        { id: 'check', label: t('listingAutoRule.stepTemuCheck') },
      ],
    };
  })();
  const verifyingText = String(progressMessage || '').trim() || verifyingCopy.fallbackMsg;
  const verifyingActiveIndex = (() => {
    const steps = verifyingCopy.steps;
    const numeric = Number(progressStep);
    let active = 0;
    if (Number.isFinite(numeric)) {
      active = Math.max(0, Math.min(steps.length - 1, numeric));
    } else {
      const msg = verifyingText;
      if (/牛皮癣/.test(msg)) {
        const found = steps.findIndex((step) => step.id === 'psoriasis');
        active = found >= 0 ? found : 0;
      } else if (/核对|Create Product|平台核验|标题规格/.test(msg)) {
        active = steps.length - 1;
      } else if (/转换|修整|带入/.test(msg)) {
        active = Math.min(1, steps.length - 1);
      }
    }
    if (/核验通过/.test(verifyingText)) return steps.length;
    return active;
  })();

  if (confirming) {
    return createPortal(
      <div className="funding-modal ipe-delete-confirm funding-modal--top ipe-listing-auto-rule-pick-layer ipe-listing-auto-rule-pick-layer--verifying">
        <div className="ipe-delete-confirm__backdrop" role="presentation" />
        <div
          className="ipe-delete-confirm__panel ipe-listing-auto-rule-pick__panel ipe-listing-auto-rule-pick__panel--verifying"
          role="dialog"
          aria-modal="true"
          aria-labelledby={TITLE_ID}
          aria-busy="true"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="ipe-listing-auto-rule-pick__verifying" role="status" aria-live="polite">
            <div className="ipe-listing-auto-rule-pick__verifying-visual" aria-hidden="true">
              <span className="ipe-listing-auto-rule-pick__verifying-ring" />
              <span className="ipe-listing-auto-rule-pick__verifying-ring ipe-listing-auto-rule-pick__verifying-ring--delay" />
              <span className="ipe-listing-auto-rule-pick__verifying-core" />
            </div>
            <h3 id={TITLE_ID}>{t('listingAutoRule.verifying')}</h3>
            <p className="ipe-listing-auto-rule-pick__verifying-lead">
              {verifyingCopy.lead}
            </p>
            <p className="ipe-listing-auto-rule-pick__verifying-msg">{verifyingText}</p>
            <ul className="ipe-listing-auto-rule-pick__verifying-steps">
              {verifyingCopy.steps.map((step, index) => {
                const state = index < verifyingActiveIndex
                  ? 'done'
                  : (index === verifyingActiveIndex ? 'active' : 'wait');
                return (
                  <li key={step.id} className={`is-${state}`}>
                    <span>{step.label}</span>
                    {state === 'done' ? <em>{t('listingAutoRule.passed')}</em> : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="funding-modal ipe-delete-confirm funding-modal--top ipe-listing-auto-rule-pick-layer">
      <div
        className="ipe-delete-confirm__backdrop"
        role="presentation"
        onClick={confirming ? undefined : onCancel}
      />
      <div
        className={`ipe-delete-confirm__panel ipe-listing-auto-rule-pick__panel${isPublish ? ' ipe-listing-auto-rule-pick__panel--publish' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shein-product-ops shein-product-ops--wide ipe-listing-auto-rule-pick__ops">
          <header className="ipe-listing-auto-rule-pick__header">
            <h3 id={TITLE_ID}>{t('listingAutoRule.pickTitle')}</h3>
            <p>
              {isPublish
                ? t('listingAutoRule.pickPublishHint')
                : t('listingAutoRule.pickIngestHint')}
              {selectedRuleCount > 0 ? t('listingAutoRule.selectedRules', { count: selectedRuleCount }) : ''}
            </p>
          </header>

          {banner ? (
            <div className="ipe-listing-auto-rule-pick__banner">
              {banner}
            </div>
          ) : (isPublish && selectedCount > 0) ? (
            <div className="ipe-listing-auto-rule-pick__banner">
              <div className="ipe-listing-auto-rule-pick__summary">
                <span className="ipe-listing-auto-rule-pick__summary-label">{t('listingAutoRule.summaryLabel')}</span>
                <strong className="ipe-listing-auto-rule-pick__summary-count">{selectedCount}</strong>
                <span className="ipe-listing-auto-rule-pick__summary-unit">{t('listingAutoRule.summaryUnit')}</span>
                <span className="ipe-listing-auto-rule-pick__summary-note">
                  {t('listingAutoRule.summaryNote')}
                </span>
              </div>
            </div>
          ) : null}

          <div className="spo-filter-bar ipe-listing-auto-rule-pick__filter-bar">
            <div className="spo-filter-bar__fields">
              <div className="spo-field spo-field--inline spo-field--category">
                <span className="spo-field__label">{t('listingAutoRule.category')}</span>
                <CategorySelect
                  categories={filterCategories}
                  value={filterDraft.category}
                  disabled={confirming || filterCatalogLoading}
                  placeholder={filterCatalogLoading ? t('listingAutoRule.categoryLoading') : t('listingAutoRule.allCategories')}
                  emptyText={t('listingAutoRule.emptyCategory')}
                  onChange={(nextId) => setFilterDraft((prev) => ({
                    ...prev,
                    category: String(nextId || '').trim(),
                  }))}
                />
              </div>
              <div className="spo-field spo-field--inline spo-field--shop-name">
                <span className="spo-field__label">{t('listingAutoRule.shopName')}</span>
                <SearchableSelect
                  options={shopNameFilterOptions}
                  value={filterDraft.shopId}
                  allowClear
                  disabled={confirming || shopsLoading}
                  placeholder={shopsLoading ? t('listingAutoRule.shopsLoading') : t('listingJobs.allShops')}
                  emptyText={t('listingAutoRule.noShopMatch')}
                  onChange={(nextId) => setFilterDraft((prev) => ({
                    ...prev,
                    shopId: String(nextId || '').trim(),
                  }))}
                />
              </div>
              <div className="spo-field spo-field--inline spo-field--filter-sm">
                <span className="spo-field__label">{t('listingAutoRule.shopType')}</span>
                <select
                  className="spo-select"
                  value={filterDraft.shopType}
                  disabled={confirming}
                  onChange={(e) => setFilterDraft((prev) => ({
                    ...prev,
                    shopType: e.target.value,
                    category: '',
                  }))}
                >
                  <option value="">{t('listingAutoRule.all')}</option>
                  {listShopTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <SheinTextBatchFilter
                label={t('listingAutoRule.ruleName')}
                fieldClassName="spo-field--rule-name"
                value={filterDraft.ruleName}
                disabled={confirming}
                placeholder={t('listingAutoRule.ruleNamePh')}
                onChange={(next) => setFilterDraft((prev) => ({ ...prev, ruleName: next }))}
                onEnter={onQuery}
              />
              <SheinTextBatchFilter
                label={t('listingAutoRule.productName')}
                fieldClassName="spo-field--product-name"
                value={filterDraft.productName}
                disabled={confirming}
                placeholder={t('listingAutoRule.productNamePh')}
                onChange={(next) => setFilterDraft((prev) => ({ ...prev, productName: next }))}
                onEnter={onQuery}
              />
            </div>
            <div className="spo-filter-bar__actions">
              <button
                type="button"
                className="spo-btn spo-btn--secondary spo-btn--sm"
                disabled={confirming}
                onClick={onReset}
              >
                {t('listingJobs.reset')}
              </button>
              <button
                type="button"
                className="spo-btn spo-btn--primary spo-btn--sm"
                disabled={confirming}
                onClick={onQuery}
              >
                {t('listingJobs.query')}
              </button>
            </div>
          </div>

          <div className="spo-status-tabs ipe-listing-auto-rule-pick__platform-tabs" role="tablist" aria-label={t('listingAutoRule.platformDim')}>
            {PLATFORM_DIMENSION_TABS.map((tab) => {
              const active = String(selectedPlatformFilter || '') === String(tab.value || '');
              const platformKey = normalizePlatformCode(tab.value) || String(tab.value || '').trim();
              const platformColor = platformKey
                ? (PLATFORM_COLOR_MAP[platformKey] || PLATFORM_COLOR_MAP[tab.value] || '')
                : '';
              return (
                <button
                  key={tab.value || 'all-platform'}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}${platformKey ? ` spo-status-tab--platform-${String(platformKey).toLowerCase()}` : ''}`}
                  style={platformColor ? {
                    color: platformColor,
                    ...(active ? { borderBottomColor: platformColor } : null),
                  } : undefined}
                  disabled={confirming}
                  onClick={() => onPlatformTabChange(tab.value)}
                >
                  {tab.value ? tab.label : t('shop.allPlatforms')}
                  <span className="spo-status-tab__count">{platformCounts[tab.value] ?? 0}</span>
                </button>
              );
            })}
          </div>

          <PlatformShopDimensionTabs
            shops={dimensionShops}
            value={selectedShopFilter}
            onChange={setSelectedShopFilter}
            counts={shopCounts}
            allLabel={t('listingJobs.allShops')}
            minShopsForAll={0}
            disabled={confirming}
            ariaLabel={t('listingAutoRule.shopDim')}
          />

          <div className="ipe-listing-auto-rule-pick__list-body">
            <div className="ipe-listing-auto-rule-pick__table-wrap" role="region" aria-label={t('listingAutoRule.listAria')}>
              <table className="spo-publish__table spo-drafts__table spo-certs-table ipe-listing-auto-rule-pick__table">
                <colgroup>
                  <col className="spo-certs-col--check" />
                  <col />
                  <col className="ipe-listing-auto-rule-pick-col--sites" />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" className="spo-certs-th spo-certs-th--check" aria-label={t('listingAutoRule.selectCol')} />
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colShop')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colSites')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colRule')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colMode')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colInterval')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colStatus')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colCategory')}</th>
                    <th scope="col" className="spo-certs-th">{t('listingAutoRule.colTemplate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!isPublish ? (
                    <tr
                      className={`ipe-listing-auto-rule-pick__skip-row${skipOnly ? ' is-selected' : ''}`}
                      onClick={() => {
                        if (!confirming) selectSkipOnly();
                      }}
                    >
                      <td className="spo-certs-td spo-certs-td--check">
                        <input
                          type="checkbox"
                          name="listing-auto-rule-pick-skip"
                          checked={skipOnly}
                          disabled={confirming}
                          onChange={() => selectSkipOnly()}
                          aria-label={skipTitleText}
                        />
                      </td>
                      <td className="spo-certs-td" colSpan={8}>
                        <div className="ipe-listing-auto-rule-pick__skip-copy">
                          <strong>{skipTitleText}</strong>
                          <span>{skipHintText}</span>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {pagedRules.length === 0 ? (
                    (isPublish || enrichedRules.length > 0) ? (
                      <tr>
                        <td colSpan={9} className="spo-certs-empty-cell">
                          <div className="spo-certs-empty">
                            <p className="spo-certs-empty__title">
                              {isPublish && enrichedRules.length === 0
                                ? t('listingAutoRule.emptyAvailable')
                                : t('listingAutoRule.emptyMatch')}
                            </p>
                            <p className="spo-certs-empty__hint">
                              {isPublish
                                ? t('listingAutoRule.emptyAvailableHint')
                                : t('listingAutoRule.emptyMatchHint')}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={9} className="spo-certs-empty-cell">
                          <div className="spo-certs-empty">
                            <p className="spo-certs-empty__title">{t('listingAutoRule.emptyEnabled')}</p>
                            <p className="spo-certs-empty__hint">
                              {t('listingAutoRule.emptyEnabledHint')}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )
                  ) : pagedRules.map((rule) => {
                    const value = String(rule.id);
                    const mode = normalizeListingMode(rule.listingMode);
                    const intervalOn = !!rule.listingIntervalEnabled;
                    const intervalMinutes = rule.listingIntervalMinutes || LISTING_INTERVAL_DEFAULT_MINUTES;
                    const checked = !skipOnly && selectedIds.has(value);
                    const platformText = platformLabel(rule.platformCode) || rule.platformCode || '';
                    const platformColor = resolvePlatformColor(rule.platformCode);
                    const operatingSitesLabel = formatOperatingSites(rule.operatingSites, rule.platformCode);
                    return (
                      <tr
                        key={value}
                        className={checked ? 'is-selected' : undefined}
                        onClick={() => {
                          if (!confirming) toggleRule(value);
                        }}
                      >
                        <td className="spo-certs-td spo-certs-td--check">
                          <input
                            type="checkbox"
                            name="listing-auto-rule-pick"
                            value={value}
                            checked={checked}
                            disabled={confirming}
                            onChange={() => toggleRule(value)}
                            aria-label={t('listingAutoRule.selectNamed', { name: rule.name || rule.id })}
                          />
                        </td>
                        <td
                          className="spo-certs-td"
                          title={[rule.shopName, rule.shopType, platformText].filter(Boolean).join(' · ')}
                        >
                          <div className="ipe-listing-auto-rule-pick__shop">
                            <strong>{rule.shopName || '—'}</strong>
                            <span className="ipe-listing-auto-rule-pick__shop-meta">
                              {platformText ? (
                                <span
                                  className="ipe-listing-auto-rule-pick__platform"
                                  style={platformColor ? { color: platformColor } : undefined}
                                >
                                  {platformText}
                                </span>
                              ) : null}
                              {rule.shopType ? (
                                <span className="ipe-listing-auto-rule-pick__shop-type">{localizedShopTypeLabel(t, rule.shopType)}</span>
                              ) : null}
                              {!platformText && !rule.shopType ? '—' : null}
                            </span>
                          </div>
                        </td>
                        <td className="spo-certs-td" title={operatingSitesLabel}>
                          {operatingSitesLabel || '—'}
                        </td>
                        <td className="spo-certs-td" title={rule.name || ''}>
                          <div className="ipe-listing-auto-rule-pick__rule-name">
                            <strong>{rule.name || t('listingAutoRule.ruleFallback', { id: rule.id })}</strong>
                            {rule.id != null ? (
                              <span className="ipe-listing-auto-rule-pick__rule-id">#{rule.id}</span>
                            ) : null}
                          </div>
                          <div className="ipe-listing-auto-rule-pick__modules" aria-label={t('listingAutoRule.modAria')}>
                            <span className={rule.runAiCopywriting ? 'is-on' : 'is-off'}>{t('listingAutoRule.modCopy')}</span>
                            <span className={rule.runVideoGeneration ? 'is-on' : 'is-off'}>{t('listingAutoRule.modVideo')}</span>
                            <span className={rule.runInfringement ? 'is-on' : 'is-off'}>{t('listingAutoRule.modRisk')}</span>
                          </div>
                        </td>
                        <td className="spo-certs-td">
                          {mode === LISTING_MODE_SCHEDULED ? t('listingAutoRule.modeScheduled') : t('listingAutoRule.modeImmediate')}
                        </td>
                        <td className="spo-certs-td">
                          {intervalOn ? t('listingAutoRule.minutes', { n: intervalMinutes }) : '—'}
                        </td>
                        <td className="spo-certs-td">{t('listingAutoRule.enabled')}</td>
                        <td className="spo-certs-td" title={rule.categoryPathLabel || rule.categoryId || ''}>
                          {rule.categoryPathLabel || rule.categoryId || '—'}
                        </td>
                        <td className="spo-certs-td" title={rule.productTemplateName || rule.productTemplateId || ''}>
                          {rule.productTemplateName || rule.productTemplateId || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <SheinOpsPager
              page={safePage}
              pageSize={safePageSize}
              total={totalFiltered}
              disabled={confirming}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              ariaLabel={t('listingAutoRule.pickPager')}
            />
          </div>
        </div>

        <footer className="ipe-delete-confirm__footer">
          <button
            type="button"
            className="ipe-delete-confirm__btn ipe-delete-confirm__btn--cancel"
            onClick={onCancel}
            disabled={confirming}
          >
            {t('workflowHub.cancel')}
          </button>
          <button
            type="button"
            className="ipe-delete-confirm__btn ipe-delete-confirm__btn--confirm ipe-listing-auto-rule-pick__confirm"
            onClick={handleConfirm}
            disabled={confirming || !canConfirm}
          >
            {confirming ? t('listingAutoRule.processing') : footerConfirmLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
